import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUpsellReport } from '@/lib/api/customer-status-upsell.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

type UpsellReport = {
  totalShown?: number;
  totalActedOn?: number;
  conversionRate?: number;
  revenueAdded?: string | number;
  bySuggestion?: Array<{
    suggestionId: string;
    name: string;
    shown: number;
    actedOn: number;
    conversionRate: number;
    revenueAdded: string | number;
  }>;
};

function periodParam(period: string): string {
  if (period === 'week') return '7d';
  if (period === 'quarter' || period === 'year') return '90d';
  if (period === 'month') return '30d';
  return '30d';
}

export function UpsellReportPage() {
  const f = useReportFilters();
  const reportQuery = useQuery({
    queryKey: ['upsell-report', f.period, f.storeId, f.dateFrom, f.dateTo],
    queryFn: () =>
      getUpsellReport({
        period: periodParam(f.period),
        ...(f.storeId ? { storeId: f.storeId } : {}),
      }),
  });

  const data = (reportQuery.data ?? {}) as UpsellReport;
  const rows = useMemo(() => data.bySuggestion ?? [], [data.bySuggestion]);

  if (reportQuery.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading upsell report…</div>;
  }

  if (reportQuery.isError) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        Could not load upsell report.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-950">Upsell Performance</h2>
        <p className="mt-1 text-sm text-slate-500">Suggestion impressions, conversion, and added revenue.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Shown</p>
          <p className="mt-1 text-3xl font-black text-slate-950">{data.totalShown ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Acted on</p>
          <p className="mt-1 text-3xl font-black text-slate-950">{data.totalActedOn ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Conversion</p>
          <p className="mt-1 text-3xl font-black text-primary">{data.conversionRate ?? 0}%</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Revenue added</p>
          <p className="mt-1 text-3xl font-black text-emerald-700">
            {formatCurrency(data.revenueAdded ?? 0)}
          </p>
        </div>
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-950">By suggestion</h3>
        </div>
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Suggestion</th>
              <th className="px-5 py-3">Shown</th>
              <th className="px-5 py-3">Acted on</th>
              <th className="px-5 py-3">Conversion</th>
              <th className="px-5 py-3 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.suggestionId}>
                <td className="px-5 py-4 font-semibold text-slate-950">{row.name}</td>
                <td className="px-5 py-4 text-slate-600">{row.shown}</td>
                <td className="px-5 py-4 text-slate-600">{row.actedOn}</td>
                <td className="px-5 py-4 text-slate-600">{row.conversionRate}%</td>
                <td className="px-5 py-4 text-right font-semibold text-slate-950">
                  {formatCurrency(row.revenueAdded)}
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                  No upsell interactions in this period.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}
